import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

type PromptRequest = {
  kind: "prompt";
  title: string;
  label: string;
  value: string;
  confirmLabel: string;
};
type ConfirmRequest = { kind: "confirm"; title: string; label: string; confirmLabel: string; danger: boolean };
type Request = (PromptRequest | ConfirmRequest) & { resolve: (value: string | boolean | null) => void };

type Toast = { id: number; text: string; tone: "info" | "ai" };

type DialogApi = {
  prompt: (options: { title: string; label?: string; value?: string; confirmLabel?: string }) => Promise<string | null>;
  confirm: (options: { title: string; label?: string; confirmLabel?: string; danger?: boolean }) => Promise<boolean>;
  toast: (text: string, tone?: "info" | "ai") => void;
};

const DialogContext = createContext<DialogApi | null>(null);

export function useDialogs(): DialogApi {
  const api = useContext(DialogContext);
  if (!api) throw new Error("useDialogs must be used inside DialogProvider");
  return api;
}

/**
 * Discord renders the activity in a sandboxed iframe where window.prompt,
 * window.confirm and file downloads are blocked — so the app brings its own.
 */
export function DialogProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const [draft, setDraft] = useState("");
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextToastId = useRef(1);

  const api = useMemo<DialogApi>(
    () => ({
      prompt: ({ title, label = "", value = "", confirmLabel = "Übernehmen" }) =>
        new Promise((resolve) => {
          setDraft(value);
          setRequest({ kind: "prompt", title, label, value, confirmLabel, resolve: resolve as never });
        }),
      confirm: ({ title, label = "", confirmLabel = "Bestätigen", danger = false }) =>
        new Promise((resolve) => {
          setRequest({ kind: "confirm", title, label, confirmLabel, danger, resolve: resolve as never });
        }),
      toast: (text, tone = "info") => {
        const id = nextToastId.current++;
        setToasts((current) => [...current, { id, text, tone }]);
        setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4000);
      },
    }),
    [],
  );

  const close = useCallback(
    (value: string | boolean | null) => {
      request?.resolve(value);
      setRequest(null);
    },
    [request],
  );

  return (
    <DialogContext.Provider value={api}>
      {children}

      {request && (
        <div className="overlay" onMouseDown={() => close(request.kind === "prompt" ? null : false)}>
          <div className="dialog" onMouseDown={(event) => event.stopPropagation()}>
            <div className="label">{request.kind === "prompt" ? "Eingabe" : "Bestätigung"}</div>
            <h2>{request.title}</h2>
            {request.label && <p>{request.label}</p>}
            {request.kind === "prompt" && (
              <input
                type="text"
                autoFocus
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") close(draft.trim() ? draft.trim() : null);
                  if (event.key === "Escape") close(null);
                }}
              />
            )}
            <div className="dialog__actions">
              <button className="pill" onClick={() => close(request.kind === "prompt" ? null : false)}>
                Abbrechen
              </button>
              <button
                className={`pill pill--solid${request.kind === "confirm" && request.danger ? " pill--solid-danger" : ""}`}
                onClick={() => close(request.kind === "prompt" ? (draft.trim() ? draft.trim() : null) : true)}
              >
                {request.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="toasts">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast${toast.tone === "ai" ? " toast--ai" : ""}`}>
            {toast.text}
          </div>
        ))}
      </div>
    </DialogContext.Provider>
  );
}
